import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { Notification } from '@/hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'

/**
 * Only same-site paths are followed; anything else just marks the notification read.
 * Browsers strip tabs and newlines from URLs, so "/<TAB>/host" would become "//host";
 * any whitespace is therefore refused.
 */
const isInternalLink = (link?: string): link is string =>
  !!link && /^\/(?![/\\])/.test(link) && !/\s/.test(link)

interface Props {
  onClose: () => void
  notifications: Notification[]
  loading: boolean
  error: Error | null
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  refresh: () => Promise<void>
}

export function NotificationDropdown({
  onClose,
  notifications,
  loading,
  error,
  markAsRead,
  markAllAsRead,
  refresh,
}: Props) {
  const navigate = useNavigate()
  const open = (n: Notification) => {
    void markAsRead(n.id)
    if (isInternalLink(n.link)) {
      onClose()
      navigate(n.link)
    }
  }

  return (
    <div
      id="notification-panel"
      role="dialog"
      aria-labelledby="notification-panel-title"
      aria-busy={loading}
      tabIndex={-1}
      className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 origin-top-right animate-in fade-in zoom-in-95 duration-200 ease-out motion-reduce:[--tw-enter-scale:1]"
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h2 id="notification-panel-title" className="font-semibold">Notifications</h2>
        <div className="flex items-center gap-2">
          {notifications.some(n => !n.read_at) && (
            <button
              type="button"
              onClick={() => void markAllAsRead()}
              className="text-xs text-blue-600 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            aria-label="Close notifications"
          >
            ✕
          </button>
        </div>
      </div>
      {error && (
        <div role="alert" className="border-b border-gray-200 bg-red-50 p-4 text-sm text-red-800">
          <p>Notifications are temporarily unavailable.</p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="mt-2 font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2"
          >
            Retry notifications
          </button>
        </div>
      )}
      <div className="max-h-96 overflow-y-auto" aria-label="Notification list">
        {loading ? (
          <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
            <span className="sr-only">Loading notifications</span>
            <div aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm" role="status">No notifications yet</p>
        ) : (
          <ul aria-label="Notifications">
            {notifications.map(n => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`w-full p-4 border-b border-gray-100 bg-transparent text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 transition-colors ${!n.read_at ? 'bg-blue-50' : ''}`}
                  onClick={() => open(n)}
                >
                  <span className="flex items-start gap-3">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">{n.title}</span>
                      <span className="block text-sm text-gray-600 mt-1">{n.message}</span>
                      <span className="block text-xs text-gray-400 mt-2">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </span>
                    </span>
                    {!n.read_at && <span aria-label="Unread" className="w-2 h-2 bg-blue-600 rounded-full mt-2" />}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
